import React, { useState, useEffect } from 'react';
import { Button, Title, Dialog, Container } from '@bsf/force-ui';
import { LoaderCircle } from 'lucide-react';
import { __, sprintf } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';

const GoogleFonts = () => {
    const [fonts, setFonts] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedFont, setSelectedFont] = useState(null);
    const [variantOptions, setVariantOptions] = useState([]);
    const [subsetOptions, setSubsetOptions] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const fetchFonts = () => {
        return apiFetch({
            path: `/ultimate-vc/v1/google-fonts?start=0&fetch=200&search=${encodeURIComponent(search)}`,
            headers: { 'X-WP-Nonce': uavcSettingsData.nonce },
        }).then((response) => {
            setFonts(response.fonts || []);
        });
    };

    useEffect(() => {
        fetchFonts();
    }, [search]);

    const refreshFonts = () => {
        setLoading(true);
        apiFetch({
            path: '/ultimate-vc/v1/google-fonts/refresh',
            method: 'POST',
            headers: { 'X-WP-Nonce': uavcSettingsData.nonce },
        })
            .then(() => fetchFonts())
            .finally(() => setLoading(false));
    };

    const openAddDialog = (font) => {
        setSelectedFont(font);
        const variants = (font.variants || []).map((v) => ({
            variant_value: v,
            variant_selected: false,
        }));
        const subsets = (font.subsets || []).map((s) => ({
            subset_value: s,
            subset_selected: false,
        }));
        setVariantOptions(variants);
        setSubsetOptions(subsets);
        setIsDialogOpen(true);
    };

    const toggleVariant = (index) => {
        setVariantOptions((prev) =>
            prev.map((v, i) =>
                i === index ? { ...v, variant_selected: !v.variant_selected } : v
            )
        );
    };

    const toggleSubset = (index) => {
        setSubsetOptions((prev) =>
            prev.map((s, i) =>
                i === index ? { ...s, subset_selected: !s.subset_selected } : s
            )
        );
    };

    const confirmAddFont = () => {
        if (!selectedFont) return;
        apiFetch({
            path: '/ultimate-vc/v1/google-fonts',
            method: 'POST',
            headers: { 'X-WP-Nonce': uavcSettingsData.nonce },
            data: {
                font_family: selectedFont.font_call,
                font_name: selectedFont.font_name,
                variants: variantOptions,
                subsets: subsetOptions,
            },
        }).then(() => {
            fetchFonts();
            setIsDialogOpen(false);
        });
    };

    const removeFont = (font) => {
        apiFetch({
            path: `/ultimate-vc/v1/google-fonts?font_name=${encodeURIComponent(font)}`,
            method: 'DELETE',
            headers: { 'X-WP-Nonce': uavcSettingsData.nonce },
        }).then(() => fetchFonts());
    };

    return (
        <>
            <Title description="" icon={null} iconPosition="right" size="sm" tag="h2" title={__('Google Fonts', 'ultimate_vc')} />
            <div className="box-border bg-background-primary p-6 rounded-lg" style={{ marginTop: '24px', maxWidth: '90%' }}>
                <Container.Item className="shrink flex flex-col space-y-1">
                    <div className="text-base font-semibold m-0 mb-2">
                        {__('Font Families', 'ultimate_vc')}
                    </div>
                    <div className="text-sm font-normal m-0" style={{ maxWidth: '90%', color: '#9CA3AF' }}>
                        {sprintf(
                            __(
                                'Get over 1500+ Google font options. If this is overwhelming for your clients, you can use this option to show only limited number of fonts in the block settings.',
                                'ultimate_vc'
                            ),
                        )}
                    </div>
                </Container.Item>
                <div className="flex items-center gap-2 ">
                    <input
                        type="text"
                        className="border rounded-md flex-1"
                        placeholder={__('Search Fonts', 'ultimate_vc')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                            borderColor: '#e0e0e0', // Default border color
                            outline: 'none',       // Removes the default outline
                            boxShadow: 'none',     // Removes the default box shadow
                            marginTop: '16px',
                            width: '38.75rem',
                            height: '46px',
                        }}
                    />
                    <Button
                        variant="primary"
                        className="uavc-remove-ring flex items-center justify-center"
                        style={{
                            backgroundColor: "#1A31E8",
                            transition: "background-color 0.3s ease",
                            height: '46px',
                            marginTop: '16px'
                        }}
                        onClick={refreshFonts}
                        disabled={loading}
                        icon={loading ? <LoaderCircle className="animate-spin" size={16} /> : null}
                        iconPosition="left"
                    >
                        {loading ? __('Refreshing', 'ultimate_vc') : __('Refresh List', 'ultimate_vc')}
                    </Button>
                </div>

                <div
                    className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-2 gap-2 "
                    style={{
                        maxHeight: '31.25rem',
                        overflowY: 'auto',
                        paddingRight: '8px',
                        marginTop: '35px',
                    }}
                >
                    {fonts.map((font) => (
                        <div
                            key={font.font_name}
                            className="flex items-center justify-between p-4 border rounded-lg shadow-md bg-white"
                            style={{ fontFamily: `'${font.font_name}'` }}
                        >
                            <span className="font-medium text-sm">{font.font_name}</span>
                            {font.selected === 'true' ? (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="font-figtree uavc-remove-ring"
                                    style={{
                                        backgroundColor: "#1A31E8",
                                        transition: "background-color 0.3s ease",
                                    }}
                                    onClick={() => removeFont(font.font_name)}
                                >
                                    {__("Remove", "ultimate_vc")}
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    className="font-figtree uavc-remove-ring"
                                    style={{
                                        backgroundColor: "#1A31E8",
                                        transition: "background-color 0.3s ease",
                                    }}
                                    onClick={() => openAddDialog(font)}
                                >
                                    {__("Add", "ultimate_vc")}
                                </Button>
                            )}
                        </div>
                    ))}
                </div>


                <div className="flex flex-col cursor-pointer">
                    <Dialog
                        design="default"
                        exitOnEsc
                        scrollLock
                        style={{ paddingTop: '100px'}}
                        className='m-0 gap-0'
                        open={isDialogOpen} // Ensure Dialog is controlled by state
                        setOpen={setIsDialogOpen} // Synchronize state
                    >

                        <Dialog.Backdrop />
                        <Dialog.Panel>
                            <Dialog.Header>
                                <div className="flex items-center justify-between">
                                    <Dialog.Title>
                                        {__('Select Font Options', 'ultimate_vc')}
                                    </Dialog.Title>
                                    <Dialog.CloseButton />
                                </div>
                            </Dialog.Header>
                            <Dialog.Body>
                                <div className="">
                                    {variantOptions.length > 0 && (
                                        <div>
                                            <p className="text-sm font-medium mb-2">{__('Variants', 'ultimate_vc')}</p>
                                            <div className="flex flex-wrap gap-2">
                                                {variantOptions.map((variant, index) => (
                                                    <label key={variant.variant_value} className="flex items-center gap-1">
                                                        <input
                                                            type="checkbox"
                                                            checked={variant.variant_selected}
                                                            onChange={() => toggleVariant(index)}
                                                        />
                                                        <span>{variant.variant_value}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {subsetOptions.length > 0 && (
                                        <div>
                                            <p className="text-sm font-medium mb-2">{__('Subsets', 'ultimate_vc')}</p>
                                            <div className="flex flex-wrap gap-2">
                                                {subsetOptions.map((subset, index) => (
                                                    <label key={subset.subset_value} className="flex items-center gap-1">
                                                        <input
                                                            type="checkbox"
                                                            checked={subset.subset_selected}
                                                            onChange={() => toggleSubset(index)}
                                                        />
                                                        <span>{subset.subset_value}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Button className='uavc-remove-ring' variant="secondary" onClick={() => setIsDialogOpen(false)}>
                                    {__('Cancel', 'ultimate_vc')}
                                </Button>
                                <Button className='uavc-remove-ring' onClick={confirmAddFont}>{__('Add Font', 'ultimate_vc')}</Button>
                            </Dialog.Footer>
                        </Dialog.Panel>
                    </Dialog>
                </div>

            </div>
        </>
    );
};

export default GoogleFonts;
